import {
  Flex,
  SimpleGrid,
  Text,
  useBreakpoint,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { Bundle } from "../../../../types";
import { ReleaseCard } from "../../../cards/ReleaseCard";
import { ShoppingPurchasedThickIcon } from "../../../icons/ShoppingPurchasedThickIcon";
import { bundleItemToRelease } from "../../../../utils/bundleItemToRelease";
import { t } from "i18next";
import { useIsInReader } from "../../../../hooks/useIsInReader";

interface BundleDetailsReleasesGridProps {
  items: Bundle["items"];
  userOwnsAllItems?: boolean;
  area?: string;
}

export function BundleDetailsReleasesGrid({
  items,
  userOwnsAllItems,
  area,
}: BundleDetailsReleasesGridProps) {
  const breakpoint = useBreakpoint({ ssr: false });

  const [isAbove430] = useMediaQuery("(min-width: 430px)");
  const inReader = useIsInReader();

  const columnCount = useMemo(() => {
    // If in reader, constrain to 2 columns due to 408px modal width
    if (inReader) {
      return 2;
    }

    // Original breakpoint logic for non-reader contexts
    switch (breakpoint) {
      case "xs":
        return 2;
      case "sm":
        return isAbove430 ? 3 : 2;
      case "md":
      case "lg":
      case "xl":
      case "2xl":
      default:
        return 4;
    }
  }, [breakpoint, isAbove430, inReader]);

  // Check for purchased items
  const purchasedCount = useMemo(() => {
    if (!items) return 0;
    return items.filter((item) => item.user_owns_release).length;
  }, [items]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <VStack spacing="16px" width="100%">
      {/* Show purchased indicator if any releases are purchased */}
      {purchasedCount > 0 && !userOwnsAllItems && (
        <Flex
          align="center"
          gap="8px"
          p="12px 16px"
          bg="transparent.info.5"
          border="1px solid"
          borderColor="transparent.info.70"
          borderRadius="8px"
          width="100%"
        >
          <ShoppingPurchasedThickIcon
            boxSize="16px"
            color="transparent.info.70"
          />
          <Text fontSize="13px" color="neutral.300" fontWeight="500">
            {t("components.slideInMenu.bundleDetails.youOwnSomeReleases", {
              count: purchasedCount,
            })}
          </Text>
        </Flex>
      )}

      <SimpleGrid
        columns={columnCount}
        spacing={0}
        rowGap="24px"
        columnGap={6}
        width="100%"
      >
        {items.map((item) => {
          const releaseFromItem = bundleItemToRelease(item);

          return (
            <VStack key={item.release_id} width="100%" align="center">
              <Flex width="100%" cursor="pointer" justifyContent="center">
                <ReleaseCard
                  content={releaseFromItem}
                  area={area}
                  layout={{
                    display: {
                      include_name: false,
                      include_title: true,
                      include_author: false,
                      include_favorite: false,
                      include_match: false,
                    },
                    style: {
                      context: "releases",
                      mode: "grid",
                      size: "l",
                      rows: 1,
                      corners_top: "left",
                      corners_bottom: "left",
                      section_is_inset: false,
                    },
                  }}
                  fullWidth
                  hideBackdrop
                  showPurchasedBadge
                />
              </Flex>
            </VStack>
          );
        })}
      </SimpleGrid>
    </VStack>
  );
}
